import { ApiRest } from './Api';

export const getCountries = async () => {
  try {
    const apiRest = new ApiRest();
    return (
      await apiRest.get({
        url: '/api/v1/country/',
      })
    ).countries;
  } catch (e) {
    throw e;
  }
};

export const RequestRegister = async (data) => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.post({
      url: '/api/v1/corporate/auth/register',
      body: data,
    });
  } catch (e) {
    throw e;
  }
};

export const RequestLogin = async (data) => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.post({
      url: '/api/v1/corporate/auth/login',
      body: data,
    });
  } catch (e) {
    throw e;
  }
};

/**
 * Completa el login de un usuario con varios paneles (CORP-546). Se llama con el
 * `panelSelectionToken` que devolvió el login, no con un JWT de sesión.
 *
 * El token va **solo** en el header: el DTO del backend usa whitelist estricta
 * (`forbidNonWhitelisted: true`) y rechaza la request si `panelSelectionToken`
 * viaja también en el body.
 *
 * ⚠️ `ApiRest` lee la key `token` de `localStorage` en su constructor, que en
 * este punto todavía está vacía. Por eso el `Authorization` se pasa explícito
 * por el 5.º argumento.
 */
export const activatePanel = async ({ panelSelectionToken, corporate_id }) => {
  try {
    const apiRest = new ApiRest(
      {},
      undefined,
      undefined,
      undefined,
      `Bearer ${panelSelectionToken}`,
    );
    return await apiRest.post({
      url: '/api/v1/corporate/auth/activate-panel',
      body: { corporate_id },
    });
  } catch (e) {
    throw e;
  }
};

/**
 * Cambia de panel sin cerrar sesión (CORP-546). A diferencia de
 * `activatePanel`, va con el JWT de sesión normal, así que `ApiRest` lo toma de
 * `localStorage` por su cuenta.
 *
 * El `tokenJWT` que devuelve es NUEVO y reemplaza al anterior: los permisos, la
 * wallet y todo lo que dependa del corporate cambian con él.
 */
export const switchPanel = async ({ corporate_id }) => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.post({
      url: '/api/v1/corporate/auth/switch-panel',
      body: { corporate_id },
    });
  } catch (e) {
    throw e;
  }
};

export const RequestOtp = async (phone, code) => {
  try {
    const data = {
      phone: phone,
      country_phone_code: code,
    };
    const apiRest = new ApiRest();
    return await apiRest.post({
      url: '/api/v1/corporate/auth/get-otp',
      body: data,
    });
  } catch (e) {
    throw e;
  }
};

export const RequestOtpByEmail = async (email) => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.post({
      url: '/api/v1/corporate/auth/get-otp',
      body: { email },
      anonymous: true,
      // Un 5xx tiene que rechazar: si resolviera, la recuperación pasaría al
      // paso del código sin que se haya enviado ninguno.
      throwServerErrors: true,
    });
  } catch (e) {
    throw e;
  }
};

export const getUserId = async (location) => {
  try {
    const data = {
      userLocation: {
        latitude: location.lat,
        longitude: location.lng,
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    const apiRest = new ApiRest();
    return await apiRest.post({
      url: '/api/v1/corporate/auth/register-as-a-rides',
      body: data,
    });
  } catch (e) {
    throw e;
  }
};

export const RequestRecoveryPswd = async (data) => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.put({
      url: '/api/v1/corporate/auth/recovery-password',
      body: data,
      anonymous: true,
    });
  } catch (e) {
    throw e;
  }
};

export const updateProfile = async (data) => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.put({
      url: '/api/v1/corporate/auth/update-profile',
      body: data,
    });
  } catch (e) {
    throw e;
  }
};

export const updateMyProfile = async (data) => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.patch({
      url: '/api/v1/corporate/auth/update-my-profile',
      params: data,
    });
  } catch (e) {
    throw e;
  }
};

// Sin argumentos a propósito: se usa directo como `queryFn`, y React Query le
// pasa su contexto como primer argumento.
export const getProfile = async () => getProfileWithOptions();

// `throwServerErrors`: un 5xx se lanza en vez de la alerta global (el polling
// de la verificación del panel no debe alertar cada 10 s durante una caída).
export const getProfileWithOptions = async ({
  throwServerErrors = false,
} = {}) => {
  const apiRest = new ApiRest();
  return apiRest.get({
    url: '/api/v1/corporate/auth/get-profile',
    throwServerErrors,
  });
};

export const getKustomerToken = async () => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.get({
      url: '/api/v1/corporate/auth/kustomer-token',
      silent: true,
    });
  } catch (e) {
    throw e;
  }
};

export const updateAutoRecharge = async (data) => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.post({
      url: '/api/v1/corporate/auth/update-auto-recharge',
      body: data,
    });
  } catch (e) {
    throw e;
  }
};

export const generateApiKey = async () => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.put({
      url: '/api/v1/corporate/auth/generate-api-key',
    });
  } catch (e) {
    throw e;
  }
};

export const getApiKey = async () => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.get({
      url: '/api/v1/corporate/auth/api-key',
    });
  } catch (e) {
    throw e;
  }
};

export const testEvent = async ({ payload, url }) => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.post({
      url: '/api/v1/corporate/auth/test-webhook',
      body: { payload, url },
    });
  } catch (e) {
    throw e;
  }
};

export const bulkAddUsersWithCsv = async (data) => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.post({
      url: '/api/v1/corporate/auth/bulk-register',
      body: data,
    });
  } catch (e) {
    throw e;
  }
};

export const getCorporateByPhone = async (data) => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.post({
      url: '/api/v1/corporate/auth/get-corporate-by-phone',
      body: data,
    });
  } catch (e) {
    throw e;
  }
};

export const requestValidateEmailAndPhone = async (data) => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.post({
      url: '/api/v1/corporate/auth/validate-email-and-phone',
      body: data,
    });
  } catch (e) {
    throw e;
  }
};

export const checkInviteStatus = async (userId, corporateId) => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.get({
      url: `/api/v1/corporate/users-admin/invite/check-status/${userId}?corporateId=${encodeURIComponent(
        corporateId,
      )}`,
    });
  } catch (e) {
    throw e;
  }
};

export const activateAccount = async ({
  adminUserId,
  invitationToken,
  password,
  confirmPassword,
  corporateId,
}) => {
  try {
    const apiRest = new ApiRest();
    return await apiRest.post({
      url: '/api/v1/corporate/users-admin/invite/set-password',
      body: {
        adminUserId,
        invitationToken,
        password,
        confirmPassword,
        corporateId,
      },
    });
  } catch (e) {
    throw e;
  }
};
